"use client";

import { useState, useEffect } from "react";
import { useFansUnitedSDK } from "fansunited-data-layer/client";
import { Loader2 } from "lucide-react";
interface CountryInfo {
  id: string;
  name: string;
  assets?: {
    flag?: string;
  };
}
interface ProfileData {
  name: string;
  email: string;
  avatar?: string;
  nickname?: string;
  country?: CountryInfo | null;
  gender?: string;
}
interface ProfileFormData {
  name: string;
  email: string;
  nickname: string;
  countryId: string;
  gender: string;
}
interface EditProfileTabProps {
  profileData: ProfileData;
  onSave: (data: ProfileFormData) => Promise<void>;
  labels: Record<string, string>;
}
export default function EditProfileTab({
  profileData,
  onSave,
  labels
}: EditProfileTabProps) {
  const sdk = useFansUnitedSDK();
  const [formData, setFormData] = useState<ProfileFormData>({
    name: profileData.name || "",
    email: profileData.email || "",
    nickname: profileData.nickname || "",
    countryId: profileData.country?.id || "",
    gender: profileData.gender || ""
  });
  const [isLoading, setIsLoading] = useState(false);
  const [countries, setCountries] = useState<CountryInfo[]>([]);
  const [nicknameError, setNicknameError] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  // Fetch countries from SDK
  useEffect(() => {
    const fetchCountries = async () => {
      if (!sdk) return;
      try {
        const countriesData = await sdk.profile.getCountries();
        setCountries(countriesData.sort((a, b) => a.name.localeCompare(b.name)));
      } catch (err) {
        console.error("Failed to fetch countries:", err);
      }
    };
    fetchCountries();
  }, [sdk]);

  // Sync form data when profileData changes
  useEffect(() => {
    setFormData({
      name: profileData.name || "",
      email: profileData.email || "",
      nickname: profileData.nickname || "",
      countryId: profileData.country?.id || "",
      gender: profileData.gender || ""
    });
  }, [profileData]);
  const validateNickname = (nickname: string): string | null => {
    if (!nickname) return null;
    const nicknameRegex = /^[a-zA-Z0-9_]+$/;
    if (!nicknameRegex.test(nickname)) {
      return labels.nicknameValidationError || "Nickname can only contain letters, numbers, and underscores";
    }
    return null;
  };
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const {
      name,
      value
    } = e.target;
    if (name === "nickname") {
      setNicknameError(validateNickname(value));
    }
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    setError(null);
    setSuccess(false);
  };
  const handleSelectChange = (name_0: string, value_0: string) => {
    setFormData(prev_0 => ({
      ...prev_0,
      [name_0]: value_0
    }));
    setError(null);
    setSuccess(false);
  };
  const handleSave = async () => {
    const nicknameValidationError = validateNickname(formData.nickname);
    if (nicknameValidationError) {
      setNicknameError(nicknameValidationError);
      return;
    }
    setNicknameError(null);
    setIsLoading(true);
    setError(null);
    setSuccess(false);
    try {
      await onSave(formData);
      setSuccess(true);
    } catch (err_0: unknown) {
      if (err_0 && typeof err_0 === "object" && "data" in err_0) {
        const errorData = err_0 as {
          data?: {
            error?: {
              status?: string;
              message?: string;
            };
          };
        };
        if (errorData.data?.error?.status === "nickname_already_taken") {
          setError(labels.nicknameAlreadyTaken || errorData.data.error.message || "Nickname already taken");
          return;
        }
      }
      setError(err_0 instanceof Error ? err_0.message : labels.saveError || "Failed to save profile");
    } finally {
      setIsLoading(false);
    }
  };
  const handleReset = () => {
    setFormData({
      name: profileData.name || "",
      email: profileData.email || "",
      nickname: profileData.nickname || "",
      countryId: profileData.country?.id || "",
      gender: profileData.gender || ""
    });
    setNicknameError(null);
    setError(null);
    setSuccess(false);
  };
  return <div className="space-y-6">
            {/* Success/Error Messages */}
            {success && <div className="rounded-lg bg-green-500/10 border border-green-500/20 p-3 text-sm text-green-600">
                    {labels.profileUpdated || "Profile updated successfully"}
                </div>}
            {error && <div className="rounded-lg bg-error/10 border border-error/20 p-3 text-sm text-error">
                    {error}
                </div>}

            {/* Form Fields */}
            <div className="space-y-4">
                <div className="space-y-1.5">
                    <label htmlFor="name" className="block text-sm font-medium text-foreground">
                        {labels.name || "Name"}
                    </label>
                    <input id="name" name="name" value={formData.name} onChange={handleInputChange} disabled={isLoading} className="w-full h-10 px-3 input text-sm disabled:opacity-50" />
                </div>

                <div className="space-y-1.5">
                    <label htmlFor="email" className="block text-sm font-medium text-foreground">
                        {labels.email || "Email"}
                    </label>
                    <input id="email" name="email" type="email" value={formData.email} onChange={handleInputChange} disabled={isLoading} className="w-full h-10 px-3 input text-sm disabled:opacity-50" />
                </div>

                <div className="space-y-1.5">
                    <label htmlFor="nickname" className="block text-sm font-medium text-foreground">
                        {labels.nickname || "Nickname"}
                    </label>
                    <input id="nickname" name="nickname" value={formData.nickname} onChange={handleInputChange} disabled={isLoading} placeholder={labels.nicknamePlaceholder || "Enter a nickname"} className={`w-full h-10 px-3 input text-sm disabled:opacity-50 ${nicknameError ? "[border-color:var(--error)]" : ""}`} />
                    {nicknameError && <p className="text-sm text-error">{nicknameError}</p>}
                    <p className="text-xs text-muted-foreground">
                        {labels.nicknameHint || "Only letters, numbers, and underscores are allowed"}
                    </p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-1.5">
                        <label htmlFor="country" className="block text-sm font-medium text-foreground">
                            {labels.country || "Country"}
                        </label>
                        <select id="country" value={formData.countryId} onChange={e_0 => handleSelectChange("countryId", e_0.target.value)} disabled={isLoading} className="w-full h-10 px-3 input text-sm disabled:opacity-50 cursor-pointer">
                            <option value="">{labels.selectCountry || "Select a country"}</option>
                            {countries.map(country => <option key={country.id} value={country.id}>
                                    {country.name}
                                </option>)}
                        </select>
                    </div>

                    <div className="space-y-1.5">
                        <label htmlFor="gender" className="block text-sm font-medium text-foreground">
                            {labels.gender || "Gender"}
                        </label>
                        <select id="gender" value={formData.gender} onChange={e_1 => handleSelectChange("gender", e_1.target.value)} disabled={isLoading} className="w-full h-10 px-3 input text-sm disabled:opacity-50 cursor-pointer">
                            <option value="">{labels.selectGender || "Select gender"}</option>
                            <option value="male">{labels.male || "Male"}</option>
                            <option value="female">{labels.female || "Female"}</option>
                            <option value="unspecified">{labels.ratherNotSay || "Rather not say"}</option>
                        </select>
                    </div>
                </div>
            </div>

            {/* Action Buttons */}
            <div className="flex gap-3">
                <button onClick={handleSave} disabled={isLoading || !!nicknameError} className="py-2 px-4 font-medium text-sm btn-primary cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2">
                    {isLoading && <Loader2 className="h-4 w-4 animate-spin" />}
                    {isLoading ? labels.saving || "Saving..." : labels.saveChanges || "Save Changes"}
                </button>
                <button onClick={handleReset} disabled={isLoading} className="py-2 px-4 border border-border rounded-lg font-medium text-sm text-foreground hover:bg-muted transition cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed">
                    {labels.reset || "Reset"}
                </button>
            </div>
        </div>;
}